import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { voiceTurn, b64ToUrl } from "@/lib/voiceTurn";
import { toast } from "sonner";
import { useSpeech } from "@/components/SpeechPlayer";
import { startLevelMeter } from "@/lib/orbLevel";
import { sceneCue, motifFor } from "@/lib/sceneBus";
import { useI18n } from "@/lib/i18n";

/* One-click voice session: wake word ("Jarvis") → short spoken ack → record → silence → STT (whisper-1) → Luna → TTS → next turn.
   Wake detection = browser SpeechRecognition (Chrome/Edge; Chrome sends audio to Google servers — NOT local).
   Without SpeechRecognition: speech-level-triggered turns (no wake word). NOT GPT-Live.
   All loop logic reads the latest props/hook through refs — no stale closures. */
const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const WAKE = /\bjarvis\b|\bcarvis\b|\bjarvıs\b|\bcervis\b/i;
const END = /\b(bitir|durdur|tamam yeter|yeter|teşekkürler bu kadar|görüşürüz|kapat)\b/i;
const ACK_TEXT = "Dinliyorum.";
export const TURN = { SILENCE_MS: 1000, SILENCE_SHORT_MS: 1500, SILENCE_PUNCT_MS: 650, NO_SPEECH_MS: 8000, FIRST_NO_SPEECH_MS: 6000, MAX_TURN_MS: 20000, SPEECH_LVL: 0.18, MIN_BLOB: 2500 };
let ackUrl = null; // cached ack audio for this page load (one TTS call)
const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=";

// Resolve true when the browser allows programmatic audio playback (must be started inside the user gesture).
const unlockAudio = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ctxP = AC ? (async () => { try { const c = new AC(); await c.resume(); await c.close(); return true; } catch (e) { return false; } })() : Promise.resolve(true);
  const elP = (async () => { try { const a = new Audio(SILENT_WAV); a.volume = 0; await a.play(); a.pause(); return true; } catch (e) { return e?.name !== "NotAllowedError"; } })();
  return Promise.all([ctxP, elP]).then(([a, b]) => a && b);
};

const micError = (e) => {
  const n = e?.name || "";
  if (/NotAllowed|PermissionDenied|Security/.test(n)) return "Mikrofon izni reddedildi — adres çubuğundaki kilit simgesinden mikrofona izin verin.";
  if (/NotFound|DevicesNotFound/.test(n)) return "Mikrofon bulunamadı — bağlı bir mikrofon yok.";
  if (/NotReadable|TrackStart/.test(n)) return "Mikrofon başka bir uygulama tarafından kullanılıyor.";
  if (/Abort/.test(n)) return "Mikrofon açılırken kesildi — tekrar deneyin.";
  return `Mikrofon açılamadı (${n || "bilinmeyen hata"}).`;
};

export const VoiceSession = forwardRef(({ setOrbState, lang = "tr", onActions, compact = false, onStatus }, apiRef) => {
  const { tx } = useI18n();
  const [phase, setPhase] = useState("off"); // off|wake|ack|recording|thinking|speaking|error
  const [note, setNote] = useState("");
  const speech = useSpeech({ setOrbState, fallbackState: "idle" });
  const latest = useRef({});
  latest.current = { speech, lang, onActions, setOrbState, phase };
  const r = useRef({ stream: null, rec: null, sr: null, meter: null, busy: false, alive: false, active: false, starting: false, silenceT: null, maxT: null, levelOff: null, lastReport: null, watchdog: null });

  const set = useCallback((p, n = "") => { setPhase(p); setNote(n); }, []);
  const PHASE_STATUS = { off: "off", error: "error", starting: "starting" };
  useEffect(() => { onStatus?.({ status: PHASE_STATUS[phase] || "live", phase, note }); }, [phase, note]); // eslint-disable-line react-hooks/exhaustive-deps
  const orb = (s) => latest.current.setOrbState?.(s);

  const stopRecorder = () => {
    clearTimeout(r.current.silenceT); clearTimeout(r.current.maxT);
    try { if (r.current.rec && r.current.rec.state !== "inactive") r.current.rec.stop(); } catch (e) {}
  };

  const stopAll = useCallback(() => {
    r.current.alive = false; r.current.busy = false; r.current.active = false; r.current.starting = false;
    clearInterval(r.current.watchdog); r.current.watchdog = null;
    try { r.current.sr?.abort(); } catch (e) {}
    r.current.sr = null;
    r.current.levelOff?.(); r.current.levelOff = null;
    stopRecorder(); r.current.rec = null;
    r.current.meter?.(); r.current.meter = null;
    r.current.stream?.getTracks().forEach((t) => t.stop()); r.current.stream = null;
    latest.current.speech.stop();
    set("off"); orb("idle");
  }, [set]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => stopAll(), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = () => stopAll(); window.addEventListener("jarvis-stop-session", h); return () => window.removeEventListener("jarvis-stop-session", h); }, [stopAll]);

  const startWake = () => {
    if (!r.current.alive || r.current.busy) return;
    r.current.active = false;
    if (!SR) { listenForSpeech(); return; }
    try { r.current.sr?.abort(); } catch (e) {}
    const sr = new SR(); sr.continuous = true; sr.interimResults = true; sr.lang = latest.current.lang === "en" ? "en-US" : "tr-TR";
    sr.onresult = (e) => {
      if (!r.current.alive || r.current.busy) return;
      const res = e.results[e.results.length - 1];
      const txt = res[0].transcript.trim();
      if (!WAKE.test(txt)) return;
      const after = txt.split(WAKE).slice(1).join(" ").replace(/^[\s,.!?]+/, "").trim();
      if (res.isFinal && after.split(/\s+/).filter(Boolean).length >= 2) { try { sr.abort(); } catch (err) {} r.current.sr = null; handleCommand(after); }
      else if (res.isFinal || after.length === 0) { r.current.sr = null; try { sr.abort(); } catch (err) {} ackThenRecord(); }
    };
    sr.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") abort("Uyandırma dinlemesi için tarayıcı konuşma tanıma izni reddedildi.");
      else if (e.error === "audio-capture") abort("Konuşma tanıma mikrofona erişemedi.");
      else if (e.error === "network" || e.error === "language-not-supported") { // SR service unusable → level-based fallback (no wake word)
        if (r.current.sr === sr) { r.current.sr = null; try { sr.abort(); } catch (err) {} if (r.current.alive && !r.current.busy) listenForSpeech("Tarayıcı konuşma tanıma kullanılamıyor — konuşmaya başlayın"); }
      }
    };
    sr.onend = () => { if (r.current.alive && !r.current.busy && r.current.sr === sr) { try { sr.start(); } catch (e) {} } };
    try { sr.start(); r.current.sr = sr; set("wake", "'Jarvis' deyin — ardından isteğinizi söyleyin"); orb("wake_listening"); }
    catch (e) { abort(`Uyandırma dinlemesi başlatılamadı (${e?.name || "hata"}).`); }
  };

  // Fallback (no SpeechRecognition): wait for real speech level, then record a turn. No wake word.
  const listenForSpeech = (msg = "Uyandırma kelimesi bu tarayıcıda yok — konuşmaya başlayın") => {
    set("wake", msg); orb("wake_listening");
    let hits = 0;
    r.current.levelOff?.();
    const h = (e) => { if (e.detail > 0.25 && ++hits > 3 && r.current.alive && !r.current.busy) { r.current.levelOff?.(); r.current.levelOff = null; recordCommand(); } };
    window.addEventListener("jarvis-level", h);
    r.current.levelOff = () => window.removeEventListener("jarvis-level", h);
  };

  // Wake-only ("Jarvis") → short spoken "Dinliyorum." (cached, one TTS call per page load) → record.
  const ackThenRecord = async () => {
    if (!r.current.alive || r.current.busy) return;
    r.current.busy = true;
    const sp = latest.current.speech;
    if (!ackUrl) { try { ackUrl = await sp.fetchSpeech(ACK_TEXT); } catch (e) { ackUrl = null; } }
    if (!r.current.alive) return;
    if (ackUrl) { set("ack", ACK_TEXT); await sp.playUrl("ack", ackUrl, { wait: true, revoke: false }); }
    if (!r.current.alive) return;
    recordCommand(true);
  };

  // Adaptive end-of-turn: browser endpointer (SR final / speechend) OR level silence whose length adapts to
  // utterance duration and trailing punctuation in the interim transcript. Not a blind fixed timeout.
  const recordCommand = (fromAck = false) => {
    if (!r.current.alive || !r.current.stream) return;
    if (r.current.busy && !fromAck && r.current.rec?.state === "recording") return;
    r.current.busy = true;
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
    const rec = new MediaRecorder(r.current.stream, { mimeType: mime });
    const chunks = [];
    let spoke = false, quietSince = null, speechStart = 0, interim = "", epFinal = false, ep = null;
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      window.removeEventListener("jarvis-level", onLvl);
      try { ep?.abort(); } catch (e) {}
      if (!r.current.alive) return;
      const blob = new Blob(chunks, { type: "audio/webm" });
      if (!spoke || blob.size < TURN.MIN_BLOB) { // nothing said → no STT call, back to wake
        r.current.busy = false; set("wake", r.current.active ? "Konuşma algılanmadı — bekleme modu" : "Konuşma algılanmadı — 'Jarvis' deyin"); startWake(); return;
      }
      runTurn({ blob, endpoint: epFinal ? "browser" : "silence", speechMs: Date.now() - speechStart });
    };
    const silenceNeeded = () => {
      if (/[.!?]\s*$/.test(interim)) return TURN.SILENCE_PUNCT_MS;
      return Date.now() - speechStart < 1500 ? TURN.SILENCE_SHORT_MS : TURN.SILENCE_MS;
    };
    const onLvl = (e) => {
      if (rec.state === "inactive") return;
      if (e.detail > TURN.SPEECH_LVL) { if (!spoke) speechStart = Date.now(); spoke = true; quietSince = null; }
      else if (spoke) { quietSince = quietSince || Date.now(); if (Date.now() - quietSince > silenceNeeded()) stopRecorder(); }
    };
    if (SR) { // endpointer only (its transcript is NOT used as the command text)
      try {
        ep = new SR(); ep.continuous = false; ep.interimResults = true; ep.lang = latest.current.lang === "en" ? "en-US" : "tr-TR";
        ep.onresult = (ev) => { const res = ev.results[ev.results.length - 1]; interim = res[0].transcript; if (res.isFinal && spoke) { epFinal = true; stopRecorder(); } };
        ep.onspeechend = () => { if (spoke) setTimeout(() => { if (rec.state === "recording") { epFinal = true; stopRecorder(); } }, 250); };
        ep.onerror = () => {}; ep.start();
      } catch (e) { ep = null; }
    }
    window.addEventListener("jarvis-level", onLvl);
    rec.start(100); r.current.rec = rec;
    set("recording", (r.current.active ? "Dinliyor — devam edin" : "Dinliyor…") + (r.current.lastReport ? ` · ${r.current.lastReport}` : "")); orb("listening");
    r.current.maxT = setTimeout(stopRecorder, TURN.MAX_TURN_MS);
    r.current.silenceT = setTimeout(() => { if (!spoke) stopRecorder(); }, r.current.active ? TURN.NO_SPEECH_MS : TURN.FIRST_NO_SPEECH_MS);
  };

  const fail = (msg, silent = false) => { sceneCue("error"); if (!silent) toast.error(msg); r.current.busy = false; r.current.active = false; set("wake", msg); if (r.current.alive) startWake(); };

  // Browser wake transcript already contains the command ("Jarvis teklifleri aç") → skip recording + STT.
  const handleCommand = (text) => {
    r.current.busy = true;
    if (END.test(text) && text.split(/\s+/).length <= 4) { r.current.active = false; r.current.busy = false; set("wake", "Bekleme modu"); startWake(); return; }
    runTurn({ text, endpoint: "wake" });
  };

  const TOOL_LABEL = { open_screen: "Ekran açılıyor", find_records: "Kayıtlar aranıyor", open_record: "Kayıt açılıyor", get_today_summary: "Bugün özetleniyor" };

  // One SSE request: (STT) → Luna stream (tools) → per-sentence TTS, played in order as it arrives.
  const runTurn = async ({ blob, text, endpoint, speechMs = 0 }) => {
    const sp = latest.current.speech;
    const tEnd = performance.now();
    r.current.active = true;
    set("thinking", blob ? "Yazıya çevriliyor…" : `"${text}"`); orb("thinking");
    const queue = []; let streamDone = false, playing = false, firstPlay = 0, idx = 0, spokenAny = false, err = null, finalText = "", bye = false, server = null;
    const ctrl = new AbortController();
    const finish = () => {
      if (!r.current.alive) return;
      r.current.busy = false;
      if (firstPlay) {
        const firstAudioS = ((firstPlay - tEnd) / 1000).toFixed(1);
        r.current.lastReport = `ilk ses ${firstAudioS} s`;
        console.info("[jarvis voice turn]", { endpoint, speechMs, client_first_audio_ms: Math.round(firstPlay - tEnd), server });
        window.__jarvisLastTurn = { endpoint, speechMs, client_first_audio_ms: Math.round(firstPlay - tEnd), server };
      }
      if (bye) { r.current.active = false; set("wake", "Bekleme modu"); startWake(); return; }
      if (spokenAny || finalText) sceneCue("ok"); // real completion only
      if (err && !spokenAny) { if (err.stage === "stt" && err.soft) { set("recording", "Anlaşılamadı — tekrar söyleyin"); recordCommand(); return; } fail(err.message, !!err.toasted); return; }
      if (!finalText && !spokenAny) { fail("Yanıt alınamadı"); return; }
      recordCommand(); // follow-up turn: no wake word, no ack
    };
    const pump = async () => {
      if (playing) return; playing = true;
      while (queue.length) {
        const { url, txt } = queue.shift();
        set("speaking", txt.slice(0, 160));
        const ok = await sp.playUrl(`turn-${idx++}`, url, { wait: true, onPlaying: () => { if (!firstPlay) firstPlay = performance.now(); } });
        if (!r.current.alive) return;
        if (ok) spokenAny = true; else { err = { message: "Ses çalınamadı", toasted: true }; break; }
      }
      playing = false;
      if (streamDone) finish();
    };
    try {
      await voiceTurn({ blob, text, lang: latest.current.lang }, (ev) => {
        if (!r.current.alive) { ctrl.abort(); return; }
        if (ev.type === "transcript") set("thinking", `"${ev.text}"`);
        else if (ev.type === "bye") bye = true;
        else if (ev.type === "tool") { set("thinking", `${TOOL_LABEL[ev.name] || ev.name}…`); sceneCue("scan", motifFor(ev.name)); }
        else if (ev.type === "actions") latest.current.onActions?.(ev.actions);
        else if (ev.type === "audio") { queue.push({ url: b64ToUrl(ev.b64), txt: ev.text }); pump(); }
        else if (ev.type === "error") { err = { ...ev, toasted: !ev.soft }; if (!ev.soft) toast.error(ev.message); }
        else if (ev.type === "done") { finalText = ev.text; server = ev.timings; }
      }, ctrl.signal);
    } catch (e) { if (!r.current.alive) return; fail(e.message || "İstek başarısız"); return; }
    streamDone = true;
    if (!playing) finish();
  };

  const abort = (msg) => { stopAll(); set("error", msg); toast.error(msg); };

  // Start = one user gesture: unlock audio playback → ask mic (visible pending state) → start wake listening.
  const start = async () => {
    if (r.current.starting) return;
    if (!navigator.mediaDevices?.getUserMedia) { abort(window.isSecureContext ? "Bu tarayıcı mikrofon erişimini desteklemiyor." : "Mikrofon için güvenli bağlantı (HTTPS) gerekir."); return; }
    if (!window.MediaRecorder) { abort("Bu tarayıcı ses kaydını desteklemiyor (MediaRecorder yok)."); return; }
    if (document.featurePolicy?.allowsFeature && !document.featurePolicy.allowsFeature("microphone")) {
      abort("Bu gömülü önizleme mikrofona izin vermiyor — uygulamayı yeni sekmede açın."); return;
    }
    r.current.starting = true;
    set("starting", "Mikrofon izni isteniyor…"); orb("wake_listening");
    const unlock = unlockAudio(); // must be called synchronously inside the click
    let stream;
    try { const mid = localStorage.getItem("jarvis-mic-id"); stream = await navigator.mediaDevices.getUserMedia({ audio: mid ? { deviceId: { exact: mid } } : true }); }
    catch (e) { r.current.starting = false; abort(micError(e)); return; }
    if (!r.current.starting) { stream.getTracks().forEach((t) => t.stop()); return; } // cancelled while prompt was open
    r.current.starting = false;
    r.current.stream = stream; r.current.alive = true;
    r.current.meter = startLevelMeter(stream);
    const unlocked = await unlock;
    if (!r.current.alive) return;
    if (!unlocked) toast.warning("Tarayıcı ses çalmayı henüz onaylamadı — yanıt sesi çalmazsa sayfaya bir kez tıklayın.");
    latest.current.speech.fetchStatus?.(); // warm /tts/status (no credits)
    startWake();
    // Keep-alive watchdog: once started, the session stays awake for the whole tab session. If wake listening
    // silently drops (SR engine death with no onend/onerror), re-arm it so "Jarvis" keeps working WITHOUT the
    // user pressing the orb again. Never promises background/locked-screen listening (tab must stay open).
    clearInterval(r.current.watchdog);
    r.current.watchdog = setInterval(() => {
      if (!r.current.alive || r.current.busy || r.current.starting) return;
      const armed = !!r.current.sr || !!r.current.levelOff || r.current.rec?.state === "recording";
      if (!armed) startWake();
    }, 4000);
    if (r.current.alive && !ackUrl) latest.current.speech.fetchSpeech(ACK_TEXT).then((u) => { ackUrl = u; }).catch(() => {});
  };

  const isOn = () => { const p = latest.current.phase; return p !== "off" && p !== "error"; };
  const toggle = useCallback(() => { // one session only: rapid double taps collapse into a single action
    if (r.current.toggling) return;
    r.current.toggling = true;
    setTimeout(() => { r.current.toggling = false; }, 600);
    if (isOn()) stopAll(); else start();
  }, [stopAll]); // eslint-disable-line react-hooks/exhaustive-deps
  useImperativeHandle(apiRef, () => ({ start, stop: stopAll, toggle, isOn }), [toggle, stopAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const on = phase !== "off" && phase !== "error";
  // a real failure must never be silent, but it is a short toast — not permanent text on the scene
  useEffect(() => { if (phase === "error" && note) toast.error(note); }, [phase, note]);
  return (
    <div className="flex flex-col items-center gap-2" data-testid="voice-session">
      {on && compact && ( // scene (and its orb) hidden: keep access to stopping the running session
        <button onClick={stopAll} data-testid="voice-stop-inline"
          className="text-[0.66rem] px-3 py-1 rounded-full border border-red-400/35 text-red-200 hover:bg-red-500/10">
          {tx("Konuşmayı durdur")}
        </button>
      )}
      {/* Only a REAL failure is shown (and toasted); a plain idle/disconnected state shows nothing. */}
      {phase === "error" && (
        <div className="text-[0.7rem] text-center max-w-md text-red-300" data-testid="voice-session-status" data-phase={phase}>
          {note} — {tx("tekrar denemek için JARVIS'e dokunun")}
        </div>
      )}
      {phase !== "error" && <span className="sr-only" data-testid="voice-session-status" data-phase={phase}>{note}</span>}
    </div>
  );
});
VoiceSession.displayName = "VoiceSession";
